import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const PriceElasticityOfDemandCalculator = () => {
  const [initialPrice, setInitialPrice] = useState<string>('');
  const [finalPrice, setFinalPrice] = useState<string>('');
  const [initialQuantity, setInitialQuantity] = useState<string>('');
  const [finalQuantity, setFinalQuantity] = useState<string>('');
  const [result, setResult] = useState<number | null>(null);

  const calculateElasticity = () => {
    const p1 = parseFloat(initialPrice);
    const p2 = parseFloat(finalPrice);
    const q1 = parseFloat(initialQuantity);
    const q2 = parseFloat(finalQuantity);

    if (isNaN(p1) || isNaN(p2) || isNaN(q1) || isNaN(q2)) {
      return;
    }

    // Calculate percentage changes
    const percentageChangeInQuantity = ((q2 - q1) / q1) * 100;
    const percentageChangeInPrice = ((p2 - p1) / p1) * 100;

    // Calculate price elasticity of demand
    const elasticity = Math.abs(percentageChangeInQuantity / percentageChangeInPrice);
    setResult(elasticity);
  };

  const interpretElasticity = (elasticity: number) => {
    if (elasticity > 1) {
      return "Elastic demand: Demand is highly sensitive to price changes";
    } else if (elasticity < 1) {
      return "Inelastic demand: Demand is relatively insensitive to price changes";
    } else {
      return "Unit elastic demand: Changes in price and demand are proportional";
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Price Elasticity of Demand Calculator | Marketing Calculator Hub</title>
        <meta name="description" content="Calculate price elasticity of demand to understand how price changes affect demand for your products or services. Make data-driven pricing decisions." />
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">Price Elasticity of Demand Calculator</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Initial Price
            </label>
            <input
              type="number"
              value={initialPrice}
              onChange={(e) => setInitialPrice(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter initial price"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Final Price
            </label>
            <input
              type="number"
              value={finalPrice}
              onChange={(e) => setFinalPrice(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter final price"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Initial Quantity
            </label>
            <input
              type="number"
              value={initialQuantity}
              onChange={(e) => setInitialQuantity(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter initial quantity"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Final Quantity
            </label>
            <input
              type="number"
              value={finalQuantity}
              onChange={(e) => setFinalQuantity(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter final quantity"
            />
          </div>
        </div>

        <button
          onClick={calculateElasticity}
          className="mt-6 bg-indigo-600 text-white font-bold py-2 px-4 rounded hover:bg-indigo-700 focus:outline-none focus:shadow-outline w-full"
        >
          Calculate Elasticity
        </button>

        {result !== null && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Result:</h3>
            <p className="text-gray-600">Price Elasticity of Demand: {result.toFixed(2)}</p>
            <p className="text-gray-600 mt-2">{interpretElasticity(result)}</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Understanding Price Elasticity of Demand</h2>
        <div className="space-y-4">
          <p className="text-gray-600">
            Price elasticity of demand measures how responsive demand is to changes in price. It helps businesses understand how price changes might affect their sales and revenue.
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2">
            <li>Elasticity {'>'} 1: Demand is elastic (sensitive to price changes)</li>
            <li>Elasticity {'<'} 1: Demand is inelastic (less sensitive to price changes)</li>
            <li>Elasticity = 1: Unit elastic (proportional changes)</li>
          </ul>
          <p className="text-gray-600">
            This metric is crucial for pricing strategies, as it helps determine how price changes will impact revenue and market share.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PriceElasticityOfDemandCalculator;
